import React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { User } from '../../../../../types/user/user';

type Props = {
  open: boolean;
  onClose: () => void;
  user: User | null;
};

const UserInfoModal = ({ open, onClose, user }: Props) => {
  const { t } = useTranslation();

  const labelMap: Record<string, string> = {
    firstName: t('system.user.info.firstName'),
    lastName: t('system.user.info.lastName'),
    email: t('system.user.info.email'),
    phone: t('system.user.info.phone'),
    nationality: t('system.user.info.nationality'),
    city: t('system.user.info.city'),
    street1: t('system.user.info.street1'),
    street2: t('system.user.info.street2'),
    postalCode: t('system.user.info.postalCode'),
    region: t('system.user.info.region'),
    role: t('system.user.info.role'),
    userId: t('system.user.userid'),
    iban: t('system.user.iban')
  };

  if (!user) {
    return null;
  }

  const {
    deleted,
    deleteReason,
    deletedAt,
    deletedBy,
    customDeleteReason,
    enabled,
    mfaEnabled,
    kycStatus,
    ...otherFields
  } = user;

  const renderBoolean = (value: boolean) =>
      value
          ? <span className="text-green-500">{t('system.user.info.yes')}</span>
          : <span className="text-red-500">{t('system.user.info.no')}</span>;

  const renderKycStatus = (status?: string) => {
    switch (status) {
      case 'APPROVED':
        return <span className="text-green-500 font-semibold">Verified</span>;
      case 'PENDING':
      case 'REVIEW':
        return <span className="text-yellow-500 font-semibold">In review</span>;
      case 'DECLINED':
        return <span className="text-red-500 font-semibold">Declined</span>;
      default:
        return <span className="text-gray-400 font-semibold">Not verified</span>;
    }
  };

  const duplicatedUserIds = Array.from(
      new Set<number>(
          kycStatus?.assumedDuplicatedUsers?.map(d => d.duplicatedUserId) ?? []
      )
  );

  return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <div className="relative">
          <DialogTitle>{t('system.user.info.title')}</DialogTitle>
          <IconButton
              aria-label="close"
              onClick={onClose}
              className="!absolute right-3 top-3 text-blue-600 hover:text-blue-800"
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent>
          <div className="max-w-md w-full space-y-2">
            {Object.entries(otherFields).map(([key, value]) => {
              const label = labelMap[key];
              if (!label) return null;

              return (
                  <div key={key} className="text-sm text-left text-gray-700 dark:text-gray-200">
                    <strong>{label}:</strong> {String(value ?? '-')}
                  </div>
              );
            })}

            <div className="flex text-sm">
              <strong>{t('system.user.info.enabled')}:</strong>&nbsp;
              {renderBoolean(enabled)}
            </div>

            <div className="flex text-sm">
              <strong>{t('system.user.info.mfaEnabled')}:</strong>&nbsp;
              {renderBoolean(mfaEnabled)}
            </div>

            <div className="flex text-sm font-semibold">
              <div>{t('system.user.kyc')}: {renderKycStatus(kycStatus?.status)}</div>
              {duplicatedUserIds.length > 0 && (
                  <div className="ml-1">
                    {duplicatedUserIds.map(id => (
                        <div key={id} className="text-red-400">#{id}</div>
                    ))}
                  </div>
              )}
            </div>

            {(kycStatus?.status === 'DECLINED' || kycStatus?.status === 'REVIEW') &&
                kycStatus.rejectionReason && (
                    <div className="mt-6 space-y-4">
                      {kycStatus.rejectionReason.map(reason => (
                          <div
                              key={reason.errorType}
                              className="border border-red-500/20 rounded-md p-4 text-sm"
                          >
                            <div className="font-semibold text-red-400">
                              {reason.shortDescription}
                            </div>
                            <div className="mt-1 text-red-300">
                              {reason.longDescription}
                            </div>
                            <div className="mt-2 text-xs text-gray-400">
                              Error type: {reason.errorType}
                            </div>
                          </div>
                      ))}
                    </div>
                )}

            {deleted && (
                <div className="mt-3 text-sm text-red-600">
                  <div>
                    <strong>{t('system.user.info.deleteReason')}:</strong> {deleteReason ?? '-'}
                    {customDeleteReason && ` (${customDeleteReason})`}
                  </div>
                  <div>
                    <strong>{t('system.user.info.deletedAt')}:</strong> {deletedAt ?? '-'}
                  </div>
                  <div>
                    <strong>{t('system.user.info.deletedBy')}:</strong> {deletedBy ?? '-'}
                  </div>
                </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
  );
};

export default UserInfoModal;
